import { useEffect, useRef } from 'react'
import type { LogEntry } from 'eyecontact'
import { cn, formatTime } from '../lib/cn'

const LEVEL_STYLE: Record<string, string> = {
  error: 'text-danger',
  warn: 'text-warn',
  info: 'text-accent',
  debug: 'text-faint',
}

export function EventLog({ entries }: { entries: LogEntry[] }) {
  const ref = useRef<HTMLDivElement | null>(null)
  const pinned = useRef(true)

  // Follow the tail, but stop fighting the user if they scroll up to read.
  useEffect(() => {
    const el = ref.current
    if (!el || !pinned.current) return
    el.scrollTop = el.scrollHeight
  }, [entries])

  const onScroll = () => {
    const el = ref.current
    if (!el) return
    pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24
  }

  return (
    <div
      ref={ref}
      onScroll={onScroll}
      className="scroll-thin h-44 overflow-y-auto px-3.5 py-2 font-mono text-2xs leading-[1.5]"
      role="log"
      aria-label="Gate event log"
    >
      {entries.length === 0 && (
        <p className="py-6 text-center text-faint">Nothing logged yet. Start the camera.</p>
      )}
      {entries.map((e, i) => (
        <div key={`${e.t}-${i}`} className="flex gap-2 py-px">
          <span className="shrink-0 text-faint">{formatTime(e.t)}</span>
          <span className={cn('w-9 shrink-0 uppercase', LEVEL_STYLE[e.level] ?? 'text-muted')}>
            {e.level}
          </span>
          <span className="min-w-0 break-words text-muted">{e.msg}</span>
        </div>
      ))}
    </div>
  )
}
